/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, no-restricted-properties, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
import { service } from '@ember/service';
import { action, computed } from '@ember/object';
import { not, alias } from '@ember/object/computed';
import { isEmpty } from '@ember/utils';
import DataType from '@linc-technologies/linced-common/enums/data-type';
import Scopes from '@linc-technologies/linced-common/enums/scopes';
import { optionAlias } from '@linc-technologies/linced-common/utils/options';
import { resolved } from '@linc-technologies/linced-common/utils/arrays';

export default class StaffmemberController extends Controller {
  @service
  session;

  @service
  paperToaster;

  @service
  options;

  @service
  roles;

  @alias('model.person')
  person;

  @alias('model.users.0')
  user;

  @not('user.disabled')
  isEmployed;

  @computed('user.scopes.[]')
  get hasAdminAccess() {
    return (this.get('user.scopes') ?? []).includes(Scopes.TENANT_ADMIN);
  }

  @tracked
  adminAccessUpdating = false;

  @optionAlias('hero:lock:disableHelpdeskAccess')
  helpDeskAccessDisabled;

  @alias('session.school.staffWithSupportAccess')
  staffWithSupportAccess;

  @computed('person.id', 'staffWithSupportAccess.content.@each.id')
  get hasSupportAccess() {
    // resolve the async hasMany relationship upfront.
    return resolved(this.staffWithSupportAccess).some(
      (item) => item.id === this.person.id,
    );
  }

  @tracked updatingSupportAccess = false;

  @computed(
    'hasSupportAccess',
    'staffWithSupportAccess.[]',
    'updatingSupportAccess',
    'isEmployed',
  )
  get supportAccessDisabled() {
    if (this.updatingSupportAccess) {
      // Always disable while processing a change.
      return true;
    } else if (this.hasSupportAccess) {
      // Otherwise, never disable if they already have access.
      return false;
    } else if (!this.isEmployed) {
      // Otherwise, disable if they aren't currently employed.
      return true;
    }

    // Disable if the school's limit has been reached.
    return this.staffWithSupportAccess.length >= 5;
  }

  @computed(
    'isEmployed',
    'person.{hasDirtyAttributes,fields.core:emailAddress.value}',
  )
  get employmentStatusDisabled() {
    if (this.get('person.hasDirtyAttributes')) {
      // Always lock employment status if there are unsaved changes.
      return true;
    }

    if (this.isEmployed) {
      // Always allow employment status changes if currently employed.
      return false;
    }

    // Lock employment status if there's no email address provided.
    return isEmpty(this.get('person.fields.core:emailAddress.value'));
  }

  @action
  updateEmploymentStatus(newState) {
    let user = this.user;
    let toaster = this.paperToaster;

    user.set('disabled', !newState);

    let action = newState ? 'enabled' : 'disabled';
    user
      .save()
      .then(() => {
        toaster.show(
          `${this.get('person.fullName')}'s Hero account has been ${action}.`,
          {
            duration: 4000,
            position: 'top right',
            toastClass: 'success',
          },
        );
        if (!newState) {
          // User account has been disabled.  Archive their email address.
          // This isn't critical, so don't return the promise - if it fails,
          // the user account should still remain disabled, and we shouldn't
          // tell the user that it failed.
          let currentEmail = this.person.fields.get('core:emailAddress');
          let currentEmailAddress = '';
          if (!isEmpty(currentEmail)) {
            currentEmailAddress = currentEmail.get('value');
          }
          if (!isEmpty(currentEmailAddress)) {
            let archivedEmail = this.person.fields.get(
              'core:archivedEmailAddress',
            );
            if (isEmpty(archivedEmail)) {
              this.person.addField(
                'core:archivedEmailAddress',
                DataType.STRING,
                '',
              );
              archivedEmail = this.person.fields.get(
                'core:archivedEmailAddress',
              );
            }
            archivedEmail.set('value', currentEmailAddress);
            currentEmail.set('value', '');
            this.person.save();
          }
        }
      })
      .catch(() => {
        user.set('disabled', newState);
        toaster.show(
          `${this.get(
            'person.fullName',
          )}'s Hero account could not be ${action}.`,
          {
            duration: 4000,
            position: 'top right',
            toastClass: 'warning',
          },
        );
      });
  }

  @action
  updateAccessLevel(newState) {
    let user = this.user;
    let toaster = this.paperToaster;
    this.adminAccessUpdating = true;

    if (newState) {
      user.get('scopes').pushObject(Scopes.TENANT_ADMIN);
      user.get('scopes').removeObject(Scopes.TENANT_STAFF);
    } else {
      user.get('scopes').pushObject(Scopes.TENANT_STAFF);
      user.get('scopes').removeObject(Scopes.TENANT_ADMIN);
    }

    let action = newState ? 'promoted to' : 'demoted from';
    user
      .save()
      .then(() => {
        toaster.show(
          `${this.get(
            'person.fullName',
          )} has been ${action} Hero Administrator.`,
          {
            duration: 4000,
            position: 'top right',
            toastClass: 'success',
          },
        );
      })
      .catch(() => {
        if (newState) {
          user.get('scopes').pushObject(Scopes.TENANT_STAFF);
          user.get('scopes').removeObject(Scopes.TENANT_ADMIN);
        } else {
          user.get('scopes').pushObject(Scopes.TENANT_ADMIN);
          user.get('scopes').removeObject(Scopes.TENANT_STAFF);
        }
        toaster.show(
          `${this.get(
            'person.fullName',
          )} could not be ${action} Hero Administrator.`,
          {
            duration: 4000,
            position: 'top right',
            toastClass: 'warning',
          },
        );
      })
      .finally(() => {
        this.adminAccessUpdating = false;
      });
  }

  @action
  toggleSupportAccess() {
    this.updatingSupportAccess = true;

    this.get('session.school')
      .reload()
      .then(async (school) => {
        // resolve the async hasMany relationship upfront.
        let staffWithSupportAccess = await school.staffWithSupportAccess;
        if (this.hasSupportAccess) {
          // Remove existing access.
          let existing = staffWithSupportAccess.find(
            (item) => item.id === this.person.id,
          );
          if (!isEmpty(existing)) {
            let existingIndex = staffWithSupportAccess.indexOf(existing);
            if (existingIndex !== -1) {
              staffWithSupportAccess.splice(existingIndex, 1);
            }
            return school.save().then(() => {
              this.paperToaster.show(
                `${this.get(
                  'person.fullName',
                )}'s support access has been removed.`,
                {
                  duration: 4000,
                  position: 'top right',
                  toastClass: 'success',
                },
              );
            });
          }
        } else {
          // Grant access (if limit not yet reached).
          if (staffWithSupportAccess.length >= 5) {
            // Limit already reached (in another tab / by another person?).
            this.paperToaster.show(
              `${this.get(
                'person.fullName',
              )} could not be granted support access, as your school's limit has been reached.`,
              {
                duration: 4000,
                position: 'top right',
                toastClass: 'warning',
              },
            );
          } else {
            // Limit not reached; add this person to the list.
            staffWithSupportAccess.push(this.person);
            return school.save().then(() => {
              this.paperToaster.show(
                `${this.get(
                  'person.fullName',
                )} has been granted support access.`,
                {
                  duration: 4000,
                  position: 'top right',
                  toastClass: 'success',
                },
              );
            });
          }
        }
      })
      .finally(() => {
        this.updatingSupportAccess = false;
      });
  }

  @action
  reloadUser() {
    this.model.users.update();
  }
}
